import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Switch,
  Text,
  Textarea,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { AddRegular, CopyRegular } from '@fluentui/react-icons';
import {
  ROLES_BY_POWER,
  ROLE_LABELS,
  STAFF_ACCESS_DEFAULT_HOURS,
  STAFF_ACCESS_HOUR_CHOICES,
  STAFF_ACCESS_LABELS,
  STAFF_ACCESS_MODES,
  formatRoleMapText,
  parseRoleMapText,
  type CreateSsoProviderInput,
  type Role,
  type SsoProviderView,
  type SsoSettingsView,
  type StaffAccessMode,
  type StaffAccessOverview,
  type StaffAccessRequestView,
  type UpdateSsoProviderInput,
} from '@tvmf/shared';
import { api } from '../../api';
import { Page } from '../../components/Page';
import { LoadError } from '../../components/records';
import { DangerText, MutedText } from '../../components/ui';
import { useNotify } from '../../hooks/useNotify';

const useStyles = makeStyles({
  body: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', ...shorthands.gap('14px'), ...shorthands.padding('16px') },
  head: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', ...shorthands.gap('8px', '12px') },
  grow: { flex: 1, minWidth: '200px' },
  stack: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', ...shorthands.gap('4px') },
  row: { display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', ...shorthands.gap('12px') },
  form: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', ...shorthands.gap('14px'), minWidth: 'min(520px, 100%)' },
  code: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, overflowWrap: 'anywhere' },
  uri: { display: 'flex', alignItems: 'center', ...shorthands.gap('8px') },
  hours: { width: '120px' },
  page: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', ...shorthands.gap('16px') },
});

const roleOptions = ROLES_BY_POWER.map((r) => (
  <option key={r} value={r}>
    {ROLE_LABELS[r]}
  </option>
));

function RoleSelect({ label, hint, value, onChange, disabled }: { label: string; hint?: string; value: Role; onChange: (r: Role) => void; disabled?: boolean }) {
  return (
    <Field label={label} hint={hint}>
      <Select value={value} disabled={disabled} onChange={(_, d) => onChange(d.value as Role)}>
        {roleOptions}
      </Select>
    </Field>
  );
}

const copyText = (text: string) => void navigator.clipboard?.writeText(text);

/** "Last tested ..." with what the test found, for a provider. */
function LastTest({ p }: { p: SsoProviderView }) {
  if (!p.lastTestAt) return <MutedText>Not tested yet. Test sign-in signs you in at the provider and shows what would happen, without creating anyone.</MutedText>;
  const when = new Date(p.lastTestAt).toLocaleString();
  return p.lastTestOk ? (
    <MessageBar layout="multiline" intent="success">
      <MessageBarBody>Last test worked ({when}). {p.lastTestDetail}</MessageBarBody>
    </MessageBar>
  ) : (
    <MessageBar layout="multiline" intent="warning">
      <MessageBarBody>Last test did not work ({when}). {p.lastTestDetail ?? 'Check the settings below and the audit log for the reason.'}</MessageBarBody>
    </MessageBar>
  );
}

/** Everything a provider card can do: switch it on or off, change one setting, run a test sign-in. */
function useProviderActions() {
  const qc = useQueryClient();
  const notify = useNotify();
  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateSsoProviderInput }) => api<SsoProviderView>(`/sso/providers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sso-settings'] });
      qc.invalidateQueries({ queryKey: ['sso-providers'] });
      notify('Saved');
    },
    onError: (e) => notify('Could not save', { intent: 'error', body: (e as Error).message }),
  });
  const test = useMutation({
    mutationFn: (id: string) => api<{ url: string }>(`/sso/providers/${id}/test`, { method: 'POST' }),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (e) => notify('Could not start the test', { intent: 'error', body: (e as Error).message }),
  });
  return { patch, test };
}


/** What each setting for Voxshift staff means, in plain words. */
const STAFF_ACCESS_HELP: Record<StaffAccessMode, string> = {
  allowed: 'Allowed: staff who have been given access to this installation at the hub sign in straight away, up to the role above.',
  needs_approval: `Needs approval: a staff sign-in waits until you approve it, for a time you choose (${STAFF_ACCESS_DEFAULT_HOURS} hours unless you say otherwise, at most 24). You are emailed and see the request on this page. When the time is up their access ends and they have to ask again.`,
  off: 'Off: Voxshift staff cannot sign in to this installation. Anyone who is in now is signed out.',
};

const whenText = (iso: string) => new Date(iso).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
const statusText = (r: StaffAccessRequestView): string => {
  switch (r.status) {
    case 'pending':
      return 'Waiting for you';
    case 'approved':
      return `Approved for ${r.windowHours} ${r.windowHours === 1 ? 'hour' : 'hours'}${r.decidedByName ? ` by ${r.decidedByName}` : ''}`;
    case 'denied':
      return `Denied${r.decidedByName ? ` by ${r.decidedByName}` : ''}${r.decisionNote ? ` (${r.decisionNote})` : ''}`;
    case 'expired':
      return r.windowEndsAt ? 'Time ran out' : 'Nobody answered, so it lapsed';
    case 'ended':
      return `Ended early${r.endedByName ? ` by ${r.endedByName}` : ''}`;
    default:
      return 'Closed because the setting was changed';
  }
};

/** Voxshift staff sign-ins waiting for approval, the access running now, and what happened recently (docs/SSO.md 4.3a). */
function StaffAccessCard({ mode }: { mode: StaffAccessMode }) {
  const s = useStyles();
  const qc = useQueryClient();
  const notify = useNotify();
  const q = useQuery({ queryKey: ['sso-access'], queryFn: () => api<StaffAccessOverview>('/sso/access'), refetchInterval: 15_000 });
  const [hours, setHours] = useState<Record<string, number>>({});
  const act = useMutation({
    mutationFn: ({ id, what, body }: { id: string; what: 'approve' | 'deny' | 'end'; body?: object }) => api<StaffAccessRequestView>(`/sso/access/${id}/${what}`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['sso-access'] });
      notify(v.what === 'approve' ? 'Approved' : v.what === 'deny' ? 'Denied' : 'Access ended');
    },
    onError: (e) => notify('Could not do that', { intent: 'error', body: (e as Error).message }),
  });
  const o = q.data;
  if (!o) return null;
  if (mode === 'allowed' && o.pending.length + o.windows.length + o.recent.length === 0) return null;
  return (
    <Card>
      <div className={s.body}>
        <Text weight="semibold" size={500}>
          Voxshift staff access
        </Text>
        {mode !== 'needs_approval' && <MutedText>The setting is "{STAFF_ACCESS_LABELS[mode]}", so nothing new is waiting. This is what happened before.</MutedText>}
        {mode === 'needs_approval' && o.pending.length === 0 && o.windows.length === 0 && <MutedText>Nobody is waiting and no staff access is running.</MutedText>}
        {o.pending.map((r) => (
          <div key={r.id} className={s.stack}>
            <Text weight="semibold">{r.displayName ?? r.email} is asking to sign in</Text>
            <MutedText>
              {r.email}, as {ROLE_LABELS[r.effectiveRole]}
              {r.requestedRole !== r.effectiveRole ? ` (the hub asked for ${ROLE_LABELS[r.requestedRole]}; your limit for Voxshift staff is lower)` : ''}, from {r.ip ?? 'an unknown address'}, asked at {whenText(r.requestedAt)}.
            </MutedText>
            <div className={s.row}>
              <Field label="Allow for">
                <Select
                  value={String(hours[r.id] ?? STAFF_ACCESS_DEFAULT_HOURS)}
                  onChange={(_, d) => setHours({ ...hours, [r.id]: Number(d.value) })}
                >
                  {STAFF_ACCESS_HOUR_CHOICES.map((h) => (
                    <option key={h} value={h}>
                      {h} {h === 1 ? 'hour' : 'hours'}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button appearance="primary" disabled={act.isPending} onClick={() => act.mutate({ id: r.id, what: 'approve', body: { hours: hours[r.id] ?? STAFF_ACCESS_DEFAULT_HOURS } })}>
                Approve
              </Button>
              <Button disabled={act.isPending} onClick={() => act.mutate({ id: r.id, what: 'deny' })}>
                Deny
              </Button>
            </div>
          </div>
        ))}
        {o.windows.map((r) => (
          <div key={r.id} className={s.row}>
            <div className={s.grow}>
              <Text weight="semibold">{r.displayName ?? r.email}</Text>
              <MutedText block>
                {ROLE_LABELS[r.effectiveRole]}. {statusText(r)}. Access ends at {whenText(r.windowEndsAt!)}. {r.signedIn ? 'They are signed in.' : 'They have not signed in yet.'}
              </MutedText>
            </div>
            <Button disabled={act.isPending} onClick={() => act.mutate({ id: r.id, what: 'end' })}>
              End now
            </Button>
          </div>
        ))}
        {o.recent.length > 0 && (
          <div className={s.stack}>
            <Text weight="semibold">Recently</Text>
            {o.recent.map((r) => (
              <MutedText key={r.id} block>
                {r.displayName ?? r.email}, {whenText(r.requestedAt)}: {statusText(r)}.
              </MutedText>
            ))}
          </div>
        )}
        <MutedText>
          You can only answer these if you signed in with your own account here, not through the Voxshift Hub. Everything is recorded in the audit log (sso.access.*). While staff hold an approved administrator window they can change settings and add users like any administrator, so approve only what you expect, and check Administration, Users afterwards.
        </MutedText>
      </div>
    </Card>
  );
}

/** The Voxshift Hub as a provider: off until someone here switches it on, with the two ceilings in plain words. */
function HubCard({ p }: { p: SsoProviderView }) {
  const s = useStyles();
  const { patch, test } = useProviderActions();
  const set = (body: UpdateSsoProviderInput) => patch.mutate({ id: p.id, body });
  return (
    <Card>
      <div className={s.body}>
        <div className={s.head}>
          <Text weight="semibold" size={500}>
            {p.name}
          </Text>
          <Badge appearance="tint" color={p.enabled ? 'success' : 'informative'}>
            {p.enabled ? 'On' : 'Off'}
          </Badge>
          <MutedText className={s.grow}>{p.identityCount === 0 ? 'Nobody has signed in this way yet.' : `${p.identityCount} ${p.identityCount === 1 ? 'person has' : 'people have'} signed in this way.`}</MutedText>
          <Button disabled={!p.enabled && !p.hubConnected} onClick={() => test.mutate(p.id)}>
            Test sign-in
          </Button>
        </div>
        <MutedText>
          Lets people who have an account at the Voxshift Hub open this installation with one click from the hub, or from the sign-in page here, without a second sign-in. Passwords stay at the hub. You decide here who is trusted and what they can do; sign-in with a local account always keeps working.
        </MutedText>
        {!p.hubConnected && (
          <MessageBar layout="multiline" intent="warning">
            <MessageBarBody>
              This installation is not connected to a Voxshift Hub yet. Connect it under <Link to="/admin/settings">Platform settings, Voxshift hub</Link>, then come back to switch this on.
            </MessageBarBody>
          </MessageBar>
        )}
        <Switch
          checked={p.enabled}
          disabled={patch.isPending || (!p.enabled && !p.hubConnected)}
          label={p.enabled ? 'People can sign in with the Voxshift Hub' : 'Off: nobody can sign in with the Voxshift Hub'}
          onChange={(_, d) => set({ enabled: d.checked })}
        />
        <RoleSelect
          label="Voxshift Hub staff can sign in as"
          hint="Voxshift staff and MSP engineers. They normally install and look after an installation, so this starts at Administrator. Lower it if you want them to need your help for administrator tasks."
          value={p.roleCeilingStaff}
          disabled={patch.isPending}
          onChange={(r) => set({ roleCeilingStaff: r })}
        />
        {p.roleCeilingStaff === 'SUPER_ADMIN' && p.enabled && (
          <MessageBar layout="multiline" intent="warning">
            <MessageBarBody>With this on, Voxshift staff who have been given access to this installation at the hub can sign in as Administrator. Every such sign-in is recorded and emailed to the administrators here.</MessageBarBody>
          </MessageBar>
        )}
        <Field
          label="Voxshift staff access"
          hint="What happens when a Voxshift staff member (or an engineer from your MSP) signs in with a hub account. Your own people and the local administrators are not affected."
        >
          <Select value={p.staffAccess} disabled={patch.isPending} onChange={(_, d) => set({ staffAccess: d.value as StaffAccessMode })}>
            {STAFF_ACCESS_MODES.map((m) => (
              <option key={m} value={m}>
                {STAFF_ACCESS_LABELS[m]}
              </option>
            ))}
          </Select>
        </Field>
        <MutedText>{STAFF_ACCESS_HELP[p.staffAccess]}</MutedText>
        <RoleSelect
          label="People from your own organisation with Voxshift Hub accounts can sign in as"
          hint="At most this role, whatever the hub says they should have. Starts at Engineer."
          value={p.roleCeilingCustomer}
          disabled={patch.isPending}
          onChange={(r) => set({ roleCeilingCustomer: r })}
        />
        <Switch checked={p.jitCreate} disabled={patch.isPending} label="Create an account automatically the first time someone signs in" onChange={(_, d) => set({ jitCreate: d.checked })} />
        <Switch checked={p.requireMfaClaim} disabled={patch.isPending} label="Only accept sign-ins where the hub confirms two-step verification" onChange={(_, d) => set({ requireMfaClaim: d.checked })} />
        <PasswordSwitch p={p} disabled={patch.isPending} onChange={(v) => set({ allowLocalPasswords: v })} />
        <LastTest p={p} />
      </div>
    </Card>
  );
}

function PasswordSwitch({ p, disabled, onChange }: { p: SsoProviderView; disabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <div>
      <Switch checked={p.allowLocalPasswords} disabled={disabled} label="Keep the password sign-in form for everyone" onChange={(_, d) => onChange(d.checked)} />
      {!p.allowLocalPasswords && (
        <MutedText block>
          While this provider is on, only local administrators (break-glass accounts) can sign in with a password and authenticator, through the "Administrator sign-in" link on the sign-in page. Everyone else uses single sign-on.
        </MutedText>
      )}
    </div>
  );
}

/** A customer's own provider (Microsoft Entra ID, Okta, Google Workspace...). */
function OidcCard({ p, onEdit, onDelete }: { p: SsoProviderView; onEdit: () => void; onDelete: () => void }) {
  const s = useStyles();
  const { patch, test } = useProviderActions();
  return (
    <Card>
      <div className={s.body}>
        <div className={s.head}>
          <Text weight="semibold" size={500}>
            {p.name}
          </Text>
          <Badge appearance="tint" color={p.enabled ? 'success' : 'informative'}>
            {p.enabled ? 'On' : 'Off'}
          </Badge>
          <MutedText className={s.grow}>{p.identityCount === 0 ? 'Nobody has signed in this way yet.' : `${p.identityCount} ${p.identityCount === 1 ? 'person has' : 'people have'} signed in this way.`}</MutedText>
          <Button onClick={() => test.mutate(p.id)}>Test sign-in</Button>
          <Button onClick={onEdit}>Edit</Button>
          <Button onClick={onDelete}>Remove</Button>
        </div>
        <Switch checked={p.enabled} disabled={patch.isPending} label={p.enabled ? 'People can sign in with this provider' : 'Off: nobody can sign in with this provider'} onChange={(_, d) => patch.mutate({ id: p.id, body: { enabled: d.checked } })} />
        <div className={s.stack}>
          <Text>
            Can sign in as: up to <strong>{ROLE_LABELS[p.roleCeiling]}</strong>.{' '}
            {p.roleClaim ? `The role comes from the "${p.roleClaim}" claim; anyone it does not match gets ${ROLE_LABELS[p.defaultRole]}.` : `Everyone gets ${ROLE_LABELS[p.defaultRole]} (the provider is not asked for a role).`}
          </Text>
          <MutedText>
            Provider address: <span className={s.code}>{p.issuer}</span>
          </MutedText>
          {p.emailDomains.length > 0 && <MutedText>Only email addresses at: {p.emailDomains.join(', ')}.</MutedText>}
        </div>
        <RedirectUri uri={p.redirectUri} />
        <LastTest p={p} />
      </div>
    </Card>
  );
}

function RedirectUri({ uri }: { uri: string }) {
  const s = useStyles();
  return (
    <Field label="Redirect address to register at the provider" hint="The provider asks for this as its redirect URI or reply URL. It must match exactly.">
      <div className={s.uri}>
        <span className={s.code}>{uri}</span>
        <Button size="small" appearance="subtle" icon={<CopyRegular />} aria-label="Copy the redirect address" onClick={() => copyText(uri)} />
      </div>
    </Field>
  );
}

/** Add or edit a customer's own provider. On edit, an empty secret keeps the one saved. */
function ProviderDialog({ existing, baseRedirect, onClose }: { existing: SsoProviderView | null; baseRedirect: string; onClose: () => void }) {
  const s = useStyles();
  const qc = useQueryClient();
  const notify = useNotify();
  const [name, setName] = useState(existing?.name ?? '');
  const [issuer, setIssuer] = useState(existing?.issuer ?? '');
  const [clientId, setClientId] = useState(existing?.clientId ?? '');
  const [secret, setSecret] = useState('');
  const [ceiling, setCeiling] = useState<Role>(existing?.roleCeiling ?? 'PROJECT_MANAGER');
  const [defaultRole, setDefaultRole] = useState<Role>(existing?.defaultRole ?? 'CUSTOMER');
  const [roleClaim, setRoleClaim] = useState(existing?.roleClaim ?? '');
  const [roleMapText, setRoleMapText] = useState(formatRoleMapText(existing?.roleMap ?? {}));
  const [domains, setDomains] = useState((existing?.emailDomains ?? []).join(', '));
  const [scopes, setScopes] = useState(existing?.scopes ?? 'openid email profile');
  const [jit, setJit] = useState(existing?.jitCreate ?? true);
  const [mfa, setMfa] = useState(existing?.requireMfaClaim ?? true);
  const [passwords, setPasswords] = useState(existing?.allowLocalPasswords ?? true);
  const [err, setErr] = useState<string | null>(null);

  const parsed = parseRoleMapText(roleMapText);
  const domainList = domains.split(/[\s,;]+/).filter(Boolean);
  const ready = name.trim() && issuer.trim() && clientId.trim() && (existing || secret) && parsed.ok;

  const save = useMutation({
    mutationFn: () => {
      if (!parsed.ok) throw new Error(parsed.error);
      const common = { name: name.trim(), issuer: issuer.trim(), clientId: clientId.trim(), scopes: scopes.trim(), roleCeiling: ceiling, defaultRole, roleClaim: roleClaim.trim() || null, roleMap: parsed.map, emailDomains: domainList, jitCreate: jit, requireMfaClaim: mfa, allowLocalPasswords: passwords };
      return existing
        ? api(`/sso/providers/${existing.id}`, { method: 'PATCH', body: JSON.stringify({ ...common, ...(secret ? { clientSecret: secret } : {}) } satisfies UpdateSsoProviderInput) })
        : api('/sso/providers', { method: 'POST', body: JSON.stringify({ ...common, clientSecret: secret } satisfies CreateSsoProviderInput) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sso-settings'] });
      qc.invalidateQueries({ queryKey: ['sso-providers'] });
      notify(existing ? 'Provider saved' : 'Provider added (switched off until you turn it on)');
      onClose();
    },
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{existing ? `Edit ${existing.name}` : 'Add a sign-in provider'}</DialogTitle>
          <DialogContent>
            <div className={s.form}>
              {!existing && (
                <MutedText>
                  Any OpenID Connect provider works: Microsoft Entra ID, Okta, Google Workspace, Keycloak. First register this installation there as a web application, using the redirect address shown below, then enter what the provider gives you.
                </MutedText>
              )}
              <Field label="Name" required hint='Shown on the sign-in button, for example "Contoso Microsoft account".'>
                <Input value={name} onChange={(_, d) => setName(d.value)} />
              </Field>
              <Field label="Provider address (issuer)" required hint="For Microsoft Entra ID: https://login.microsoftonline.com/<your directory (tenant) ID>/v2.0">
                <Input value={issuer} onChange={(_, d) => setIssuer(d.value)} placeholder="https://login.example.com" />
              </Field>
              <Field label="Client ID" required hint="Entra ID calls it the Application (client) ID.">
                <Input value={clientId} onChange={(_, d) => setClientId(d.value)} />
              </Field>
              <Field label="Client secret" required={!existing} hint={existing ? 'Leave empty to keep the saved secret. It is stored encrypted and never shown.' : 'Stored encrypted and never shown again.'}>
                <Input type="password" value={secret} autoComplete="off" onChange={(_, d) => setSecret(d.value)} />
              </Field>
              <RedirectUri uri={existing?.redirectUri ?? baseRedirect} />
              <RoleSelect label="Highest role this provider can give" hint="The ceiling: whatever the provider says, nobody from it gets more than this. You can lower it at any time; people above it are signed out at their next refresh (within 15 minutes)." value={ceiling} onChange={setCeiling} />
              <RoleSelect label="Role when the provider does not say" hint="Used when there is no role claim, or nothing in it matches below. Customer is the safest choice." value={defaultRole} onChange={setDefaultRole} />
              <Field label="Role claim (optional)" hint={'The name of the claim that lists a person\'s groups or app roles, for example "roles" or "groups". Leave empty to give everyone the role above.'}>
                <Input value={roleClaim} onChange={(_, d) => setRoleClaim(d.value)} />
              </Field>
              <Field label="Which values mean which role" validationState={parsed.ok ? 'none' : 'error'} validationMessage={parsed.ok ? undefined : parsed.error} hint="One per line, for example:  Voxshift Admins = Administrator">
                <Textarea value={roleMapText} rows={3} onChange={(_, d) => setRoleMapText(d.value)} />
              </Field>
              <Field label="Only these email domains (optional)" hint="Separate with commas, for example contoso.com, fabrikam.com. Empty means any address the provider vouches for.">
                <Input value={domains} onChange={(_, d) => setDomains(d.value)} />
              </Field>
              <Switch checked={jit} label="Create an account automatically the first time someone signs in" onChange={(_, d) => setJit(d.checked)} />
              <Switch checked={mfa} label="Only accept sign-ins where the provider confirms two-step verification (MFA)" onChange={(_, d) => setMfa(d.checked)} />
              <Switch checked={passwords} label="Keep the password sign-in form for everyone" onChange={(_, d) => setPasswords(d.checked)} />
              {!passwords && <MutedText>Only local administrators (break-glass accounts) will be able to sign in with a password. You need at least one before this can be saved with the provider on.</MutedText>}
              <Field label="Scopes" hint="Leave as it is unless your provider needs something different. It must include openid.">
                <Input value={scopes} onChange={(_, d) => setScopes(d.value)} />
              </Field>
              {err && <DangerText>{err}</DangerText>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!ready || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving' : existing ? 'Save' : 'Add provider'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function DeleteDialog({ p, onClose }: { p: SsoProviderView; onClose: () => void }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const del = useMutation({
    mutationFn: () => api(`/sso/providers/${p.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sso-settings'] });
      qc.invalidateQueries({ queryKey: ['sso-providers'] });
      notify(`${p.name} removed`);
      onClose();
    },
    onError: (e) => notify('Could not remove it', { intent: 'error', body: (e as Error).message }),
  });
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Remove {p.name}?</DialogTitle>
          <DialogContent>
            Everyone who signed in through it is signed out, and Voxshift forgets who they are at this provider. Their accounts stay (disable them under Users if they should not get back in). Local sign-in is not affected.
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={del.isPending} onClick={() => del.mutate()}>
              Remove provider
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/** How long a sign-in lasts and who is told about administrator sign-ins. */
function GeneralCard({ v }: { v: SsoSettingsView }) {
  const s = useStyles();
  const qc = useQueryClient();
  const notify = useNotify();
  const [hours, setHours] = useState(String(v.sessionMaxHours));
  const n = Number(hours);
  const valid = Number.isInteger(n) && n >= 1 && n <= 720;
  const save = useMutation({
    mutationFn: (body: { sessionMaxHours?: number; notifyAdminSignin?: boolean }) => api<SsoSettingsView>('/sso/general', { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: (r) => {
      qc.setQueryData(['sso-settings'], r);
      notify('Saved');
    },
    onError: (e) => notify('Could not save', { intent: 'error', body: (e as Error).message }),
  });
  return (
    <Card>
      <div className={s.body}>
        <Text weight="semibold" size={500}>
          Sign-in sessions
        </Text>
        <div className={s.row}>
          <Field label="Sign in again at the provider after (hours)" hint="Whatever the person does in between, a single sign-on session ends after this long. Default 12." validationState={valid ? 'none' : 'error'} validationMessage={valid ? undefined : 'Whole hours, 1 to 720.'}>
            <Input className={s.hours} inputMode="numeric" value={hours} onChange={(_, d) => setHours(d.value.replace(/\D/g, ''))} />
          </Field>
          <Button appearance="primary" disabled={!valid || n === v.sessionMaxHours || save.isPending} onClick={() => save.mutate({ sessionMaxHours: n })}>
            Save
          </Button>
        </div>
        <Switch
          checked={v.notifyAdminSignin}
          disabled={save.isPending}
          label="Email the administrators when someone signs in as Administrator through a provider"
          onChange={(_, d) => save.mutate({ notifyAdminSignin: d.checked })}
        />
        {v.notifyAdminSignin && !v.emailEnabled && <MutedText>Email is switched off for the whole platform (Platform settings), so these notices are not sent. Sign-ins are still recorded in the audit log.</MutedText>}
        <MutedText>
          {v.localPasswords
            ? 'Password sign-in is available to everyone.'
            : 'Password sign-in is switched off for everyone except local administrators (break-glass accounts), because a provider is on with "Keep the password sign-in form" off.'}
        </MutedText>
      </div>
    </Card>
  );
}

/** Administration > Platform settings > Single sign-on (docs/SSO.md 4.5a). */
export function AdminSso() {
  const s = useStyles();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const q = useQuery({ queryKey: ['sso-settings'], queryFn: () => api<SsoSettingsView>('/sso/settings') });
  const [editing, setEditing] = useState<SsoProviderView | 'new' | null>(null);
  const [removing, setRemoving] = useState<SsoProviderView | null>(null);
  const v = q.data;
  const tested = params.get('tested');
  const testedProvider = v?.providers.find((p) => p.id === tested);
  const dismissTest = () => {
    params.delete('tested');
    setParams(params, { replace: true });
    qc.invalidateQueries({ queryKey: ['sso-settings'] });
  };

  const hub = v?.providers.find((p) => p.kind === 'hub');
  const own = v?.providers.filter((p) => p.kind === 'oidc') ?? [];

  return (
    <Page
      platform
      narrow
      title="Single sign-on"
      subtitle="Let people sign in with an account they already have, instead of a separate password here."
      actions={
        <Button appearance="primary" icon={<AddRegular />} onClick={() => setEditing('new')}>
          Add a provider
        </Button>
      }
    >
      {q.isLoading && <Spinner label="Loading" />}
      {q.isError && <LoadError message={(q.error as Error).message} />}
      {v && (
        <div className={s.page}>
          {testedProvider && (
            <MessageBar layout="multiline" intent={testedProvider.lastTestOk ? 'success' : 'warning'}>
              <MessageBarBody>
                {testedProvider.lastTestOk ? `The test sign-in with ${testedProvider.name} worked. ` : `The test sign-in with ${testedProvider.name} did not work. `}
                {testedProvider.lastTestDetail ?? ''}
              </MessageBarBody>
              <Button size="small" onClick={dismissTest}>
                Dismiss
              </Button>
            </MessageBar>
          )}
          <MessageBar layout="multiline" intent="info">
            <MessageBarBody>
              Local accounts keep working, and local administrators (break-glass accounts) can always sign in with a password and authenticator. Nothing is switched on until you turn a provider on here. A person's role is the lower of what the provider says and the highest role you allow for it, checked at every sign-in.
            </MessageBarBody>
          </MessageBar>
          {hub && <StaffAccessCard mode={hub.staffAccess} />}
          {hub && <HubCard p={hub} />}
          {own.map((p) => (
            <OidcCard key={p.id} p={p} onEdit={() => setEditing(p)} onDelete={() => setRemoving(p)} />
          ))}
          {own.length === 0 && <MutedText>You have not added a provider of your own. Add one to let your own people sign in with Microsoft Entra ID, Okta, Google Workspace or similar.</MutedText>}
          <GeneralCard key={`${v.sessionMaxHours}-${v.notifyAdminSignin}`} v={v} />
        </div>
      )}
      {editing && v && <ProviderDialog existing={editing === 'new' ? null : editing} baseRedirect={`${v.baseUrl}/api/auth/oidc/callback`} onClose={() => setEditing(null)} />}
      {removing && <DeleteDialog p={removing} onClose={() => setRemoving(null)} />}
    </Page>
  );
}
